import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Upload, ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { image_url: "", eyebrow: "", title: "", cta_label: "" };

const BLOCKS = [
  {
    slug: "home_wealth",
    label: "Healthy Guides",
    helper: "Carte sombre — colonne gauche",
    bg: "bg-[#1A2E1A]",
    eyebrowColor: "text-[#7AC143]",
  },
  {
    slug: "home_kitchen",
    label: "Kitchen Corner",
    helper: "Carte verte — colonne droite",
    bg: "bg-[#7AC143]",
    eyebrowColor: "text-white/80",
  },
];

function CtaBlockForm({ block }) {
  const { slug, label, helper, bg, eyebrowColor } = block;
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get(`/site/cta/${slug}`);
      setData({ ...EMPTY, ...r.data });
    } catch {
      toast.error(`Impossible de charger « ${label} »`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.image_url) return toast.error("Une image est requise");
    if (!data.title.trim()) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      await api.put(`/site/cta/${slug}`, data);
      toast.success(`« ${label} » mis à jour`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/site/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Upload échoué");
      const url = json.url.startsWith("http") ? json.url : `${BACKEND_URL}${json.url}`;
      set("image_url", url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(err.message || "Erreur upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div data-testid={`home-cta-loading-${slug}`} className="text-sm text-[#4B634B] py-12 text-center">
        Chargement…
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 border border-[#E5EAE5] rounded-2xl p-5" data-testid={`home-cta-editor-${slug}`}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#7AC143]">{label}</div>
          <p className="text-xs text-[#4B634B]">{helper}</p>
        </div>

        <div>
          <Label>Image (URL ou upload)</Label>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Input
              data-testid={`home-cta-image-url-${slug}`}
              value={data.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://… ou /api/files/…"
              required
            />
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={onFile}
              className="hidden"
              data-testid={`home-cta-file-input-${slug}`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`home-cta-upload-btn-${slug}`}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Uploader
            </Button>
          </div>
          <p className="text-xs text-[#4B634B] mt-1">PNG / JPG / WebP — max 5 Mo. Format recommandé : 1600×900 px.</p>
        </div>

        <div>
          <Label>Mention en haut (eyebrow)</Label>
          <Input
            data-testid={`home-cta-eyebrow-${slug}`}
            value={data.eyebrow}
            onChange={(e) => set("eyebrow", e.target.value)}
            placeholder={`ex : ${label}`}
            maxLength={60}
          />
        </div>

        <div>
          <Label>Titre</Label>
          <Input
            data-testid={`home-cta-title-${slug}`}
            value={data.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="ex : Prenez soin de votre bien-être"
            required
            maxLength={120}
          />
        </div>

        <div>
          <Label>Libellé du bouton</Label>
          <Input
            data-testid={`home-cta-label-${slug}`}
            value={data.cta_label}
            onChange={(e) => set("cta_label", e.target.value)}
            placeholder="ex : Lire les guides"
            maxLength={40}
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            type="submit"
            className="bg-[#7AC143] hover:bg-[#67BC2A] text-white"
            disabled={saving}
            data-testid={`home-cta-save-${slug}`}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#4B634B] hover:text-[#1A2E1A] inline-flex items-center gap-1"
            data-testid={`home-cta-preview-link-${slug}`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> Voir sur la home
          </a>
        </div>
      </form>

      {/* Live preview */}
      <div>
        <Label className="block mb-3">Aperçu</Label>
        <div
          className={`relative overflow-hidden rounded-3xl ${bg} text-white p-8 min-h-[260px] flex flex-col justify-between`}
          data-testid={`home-cta-preview-${slug}`}
        >
          {data.image_url && (
            <img
              src={data.image_url}
              alt={data.title}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
          )}
          <div className="relative">
            {data.eyebrow && (
              <div className={`text-xs font-bold tracking-[0.3em] uppercase ${eyebrowColor} mb-3`}>
                {data.eyebrow}
              </div>
            )}
            <h3 className="text-2xl lg:text-3xl font-black tracking-tight">{data.title || "Titre"}</h3>
          </div>
          {data.cta_label && (
            <div className="relative inline-flex items-center gap-2 font-semibold mt-6">
              {data.cta_label} <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Editor for the two big home page CTA cards (Healthy Guides + Kitchen Corner).
 * Backed by /api/site/cta/{slug}. Open to admin AND assistant.
 */
export default function HomeCTAsEditor() {
  return (
    <div className="space-y-6" data-testid="home-ctas-editor">
      {BLOCKS.map((b) => (
        <CtaBlockForm key={b.slug} block={b} />
      ))}
    </div>
  );
}
